import { useState, useEffect } from 'react'
import clsx from 'clsx'
import { PlaceholdersAndVanishInput } from './ui/placeholders-and-vanish-input'

function useDebounceValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debouncedValue
}

interface Lodge {
  id: number
  label: string
}

interface LodgeSelectProps {
  searchUrl: string
  name: string // name for hidden input
  /** Lets a form observe the selection so it can validate it. */
  onSelectionChange?: (lodgeId: number | null) => void
  /** Marks the search box when the containing form has flagged the field. */
  invalid?: boolean
  onBlur?: () => void
}

export function LodgeSelect({
  searchUrl,
  name,
  onSelectionChange,
  invalid,
  onBlur,
}: LodgeSelectProps) {
  const [query, setQuery] = useState('')
  const [inputValue, setInputValue] = useState('')
  const debouncedQuery = useDebounceValue(query, 300)
  const [selectedLodge, setSelectedLodge] = useState<Lodge | null>(null)
  const [options, setOptions] = useState<Lodge[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!debouncedQuery) {
      setOptions([])
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)

    const url = new URL(searchUrl, window.location.origin)
    url.searchParams.set('q', debouncedQuery)

    fetch(url, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => {
        if (active) {
          // The endpoint returns already-shaped { id, label } records.
          setOptions(data)
        }
      })
      .catch(() => {
        if (active) setOptions([])
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [debouncedQuery, searchUrl])

  const handleSelect = (lodge: Lodge) => {
    setSelectedLodge(lodge)
    setInputValue(lodge.label)
    setQuery('')
    setOptions([])
    onSelectionChange?.(lodge.id)
    onBlur?.()
  }

  const placeholders = [
    'Search by lodge name or number',
    'Try "East Providence" or "2334"',
    'Enter a city, state, or lodge #',
  ]

  return (
    <div className="w-full">
      {/* No `required` here: a hidden input is exempt from browser constraint
          validation, so it never enforced anything. The containing form
          validates the selection through `onSelectionChange` instead. */}
      <input type="hidden" name={name} value={selectedLodge?.id || ''} />
      <div className="relative">
        <PlaceholdersAndVanishInput
          placeholders={placeholders}
          value={inputValue}
          onValueChange={setInputValue}
          aria-invalid={invalid ? true : undefined}
          onChange={(event) => {
            const value = event.target.value
            setQuery(value)
            if (selectedLodge) {
              setSelectedLodge(null)
              onSelectionChange?.(null)
            }
          }}
          onSubmit={() => {
            if (options.length === 1) {
              handleSelect(options[0])
            }
          }}
          as="div"
          showSubmitButton={false}
        />

        {query.trim().length > 0 && loading && (
          <div className="absolute left-0 right-0 top-full mt-2 rounded-md bg-[color:var(--theme-surface)] py-2 px-3 text-sm text-[color:var(--theme-muted)] shadow-lg ring-1 ring-[color:var(--theme-border-soft)]">
            Searching...
          </div>
        )}

        {query.trim().length > 0 && options.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-2 max-h-60 overflow-auto rounded-md bg-[color:var(--theme-surface)] py-1 text-base shadow-lg ring-1 ring-[color:var(--theme-border-soft)] focus:outline-none sm:text-sm z-50">
            {options.map((lodge) => (
              <button
                key={lodge.id}
                type="button"
                onClick={() => handleSelect(lodge)}
                className={clsx(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm text-[color:var(--theme-fg)] hover:bg-[color:var(--theme-hover)]',
                  selectedLodge?.id === lodge.id && 'font-semibold',
                )}
              >
                <span className="truncate">{lodge.label}</span>
                {selectedLodge?.id === lodge.id && (
                  <svg
                    className="h-4 w-4 text-[color:var(--theme-muted)]"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}

        {query.trim().length > 0 && options.length === 0 && !loading && (
          <div className="absolute left-0 right-0 top-full mt-2 rounded-md bg-[color:var(--theme-surface)] py-2 px-3 text-sm text-[color:var(--theme-muted)] shadow-lg ring-1 ring-[color:var(--theme-border-soft)]">
            No lodges found.
          </div>
        )}
      </div>
    </div>
  )
}
